import { AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

interface CellPosition {
  rowIndex: number;
  column: string;
}

@Component({
    selector: 'app-samples',
    templateUrl: './samples.component.html',
    styleUrls: ['./samples.component.scss'],
    standalone: false
})
export class SamplesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() dataset: any[];
  @Input() cols: any[];
  public displayedColumns = [];
  public dataSource = new MatTableDataSource<any>([]);
  public pageSize = 10;

  // Selection state
  public selectionStart: CellPosition | null = null;
  public selectionEnd: CellPosition | null = null;
  public isSelecting = false;

  // Resize observer
  private resizeObserver: ResizeObserver;
  private resizeDebounceTimer: any = null;

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    (<any>window).samples = this;
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }
  }

  private setupResizeObserver(): void {
    const element = this.elementRef.nativeElement;
    if (!element) return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }
      this.resizeDebounceTimer = setTimeout(() => {
        this.optimizePageSize();
      }, 150);
    });

    this.resizeObserver.observe(element);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadColumns();
    this.loadData();
    this.clearSelection();
  }

  optimizePageSize() {
    const parentHeight: number = (<any>document.querySelector('app-samples'))?.offsetHeight;
    var x = parentHeight;

    this.pageSize = Math.round(0.026 * x - 3);
    if (this.paginator) {
      this.paginator._changePageSize(this.pageSize);
    }
  }

  loadData() {
    const array = [];
    if (!Array.isArray(this.dataset[0])) {
      const parsed = JSON.parse(JSON.stringify(Object.assign({}, this.dataset)));
      array.push(parsed);
    } else {
      for (let item of this.dataset) {
        const parsed = JSON.parse(JSON.stringify(Object.assign({}, item)));
        array.push(parsed);
      }
    }

    this.dataSource.data = array;
    setTimeout(() => {
      this.dataSource.paginator = this.paginator;
      this.optimizePageSize();
      // Clear selection when page changes
      if (this.paginator) {
        this.paginator.page.subscribe(() => this.clearSelection());
      }
    }, 0);
  }

  loadColumns() {
    this.displayedColumns = [];
    const numColumns = Array.isArray(this.dataset[0]) ? this.dataset[0].length : this.dataset.length;

    for (let i = 0; i < numColumns; i++) {
      this.displayedColumns.push(`${i}`);
    }

    if (!this.cols || this.cols.length === 0) {
      this.cols = this.displayedColumns;
    }
  }

  getColInfo(index) {
    const meta = this.cols[index];
    return `${meta.min}, ${meta.mean}, ${meta.max}`;
  }

  stringify(data: any): string {
    if (data === null || data === undefined) return '';
    if (typeof data === 'object') return JSON.stringify(data);
    if (typeof data === 'number') {
      // Format numbers nicely - show more decimals for small numbers
      if (Number.isInteger(data)) return data.toString();
      return data.toFixed(6);
    }
    return String(data);
  }

  // Selection methods
  onCellMouseDown(event: MouseEvent, rowIndex: number, column: string) {
    event.preventDefault();
    this.selectionStart = { rowIndex, column };
    this.selectionEnd = { rowIndex, column };
    this.isSelecting = true;
  }

  onCellMouseEnter(rowIndex: number, column: string) {
    if (!this.isSelecting) return;
    this.selectionEnd = { rowIndex, column };
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.isSelecting = false;
  }

  getSelectionBounds(): { startRow: number; endRow: number; minCol: number; maxCol: number } | null {
    if (!this.selectionStart || !this.selectionEnd) return null;

    const startRow = Math.min(this.selectionStart.rowIndex, this.selectionEnd.rowIndex);
    const endRow = Math.max(this.selectionStart.rowIndex, this.selectionEnd.rowIndex);
    const startColIndex = this.displayedColumns.indexOf(this.selectionStart.column);
    const endColIndex = this.displayedColumns.indexOf(this.selectionEnd.column);
    const minCol = Math.min(startColIndex, endColIndex);
    const maxCol = Math.max(startColIndex, endColIndex);
    return { startRow, endRow, minCol, maxCol };
  }

  getCellSelectionClasses(rowIndex: number, column: string): string {
    const bounds = this.getSelectionBounds();
    if (!bounds) return '';

    const colIndex = this.displayedColumns.indexOf(column);

    const isSelected = rowIndex >= bounds.startRow && rowIndex <= bounds.endRow &&
                       colIndex >= bounds.minCol && colIndex <= bounds.maxCol;

    if (!isSelected) return '';

    const classes = ['selected'];
    if (rowIndex === bounds.startRow) classes.push('selection-top');
    if (rowIndex === bounds.endRow) classes.push('selection-bottom');
    if (colIndex === bounds.minCol) classes.push('selection-left');
    if (colIndex === bounds.maxCol) classes.push('selection-right');

    return classes.join(' ');
  }

  clearSelection() {
    this.selectionStart = null;
    this.selectionEnd = null;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Ctrl+C or Cmd+C to copy
    if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
      if (this.selectionStart && this.selectionEnd) {
        event.preventDefault();
        this.copySelectionToClipboard();
      }
    }
    // Escape to clear selection
    if (event.key === 'Escape') {
      this.clearSelection();
    }
  }

  copySelectionToClipboard() {
    const bounds = this.getSelectionBounds();
    if (!bounds) return;

    const selectedColumns = this.displayedColumns.slice(bounds.minCol, bounds.maxCol + 1);
    const rows: string[] = [];

    // Get current page data - rowIndex is relative to current page
    const pageIndex = this.paginator?.pageIndex || 0;
    const pageSize = this.paginator?.pageSize || this.pageSize;
    const pageStart = pageIndex * pageSize;

    for (let i = bounds.startRow; i <= bounds.endRow; i++) {
      const row = this.dataSource.data[pageStart + i];
      if (!row) continue;
      const cells = selectedColumns.map(col => this.stringify(row[col]));
      rows.push(cells.join('\t'));
    }

    const text = rows.join('\n');
    navigator.clipboard.writeText(text);
  }
}
